import api from "../api/api";

export type CompanyLogo = { dataUrl: string; width: number; height: number; extension: "png" | "jpeg" | "gif" };

// Loaded once and kept in memory so print windows (which must open synchronously on click)
// can include the logo without waiting on a network request.
let cachedLogo: CompanyLogo | null = null;
let loadPromise: Promise<CompanyLogo | null> | null = null;

const MAX_HEIGHT_PX = 60;
const MAX_WIDTH_PX = 200;

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

function naturalSize(dataUrl: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth || MAX_WIDTH_PX, height: img.naturalHeight || MAX_HEIGHT_PX });
    img.onerror = () => resolve({ width: MAX_WIDTH_PX, height: MAX_HEIGHT_PX });
    img.src = dataUrl;
  });
}

/** Fetches the company logo (once). Resolves to null when the company has none. */
export function loadCompanyLogo(force = false): Promise<CompanyLogo | null> {
  if (force) loadPromise = null;
  if (!loadPromise) {
    loadPromise = (async () => {
      try {
        const response = await api.get("/company-logo", { responseType: "blob" });
        const blob = response.data as Blob;
        const type = blob.type || "";
        const extension = type.includes("png") ? "png" : type.includes("gif") ? "gif" : "jpeg";
        const dataUrl = await blobToDataUrl(blob);
        const { width, height } = await naturalSize(dataUrl);
        cachedLogo = { dataUrl, width, height, extension };
      } catch {
        cachedLogo = null;
      }
      return cachedLogo;
    })();
  }
  return loadPromise;
}

export function getCachedCompanyLogo(): CompanyLogo | null {
  return cachedLogo;
}

export function clearCompanyLogoCache() {
  cachedLogo = null;
  loadPromise = null;
}

/** Display size that fits within the max box while keeping the aspect ratio. */
export function logoDisplaySize(logo: CompanyLogo) {
  const scale = Math.min(MAX_HEIGHT_PX / logo.height, MAX_WIDTH_PX / logo.width, 1);
  return { width: Math.round(logo.width * scale), height: Math.round(logo.height * scale) };
}

/** Centred logo block for the HTML of printed PDFs; empty when there is no logo. */
export function companyLogoHtml(): string {
  const logo = cachedLogo;
  if (!logo) return "";
  const { width, height } = logoDisplaySize(logo);
  return `<div style="text-align:center;margin:0 0 14px;"><img src="${logo.dataUrl}" alt="" style="width:${width}px;height:${height}px;object-fit:contain;" /></div>`;
}

type ExcelWorkbook = { addImage: (img: { base64: string; extension: "png" | "jpeg" | "gif" }) => number };
type ExcelWorksheet = {
  columnCount: number;
  getColumn: (i: number) => { width?: number };
  getRow: (i: number) => { height?: number };
  insertRow: (pos: number, values: unknown[]) => unknown;
  addImage: (id: number, range: any) => void;
  mergeCells: (top: number, left: number, bottom: number, right: number) => void;
  unMergeCells: (top: number, left: number, bottom: number, right: number) => void;
  views: Array<{ state?: string; ySplit?: number; [key: string]: unknown }>;
};

/**
 * ExcelJS's insertRow doesn't move merged ranges or frozen panes, so shift them by hand
 * when making room for the logo row.
 */
function insertTopRowPreservingLayout(sheet: ExcelWorksheet) {
  const merges: Array<{ top: number; left: number; bottom: number; right: number }> = Object.values(
    ((sheet as unknown as { _merges?: Record<string, { model: any }> })._merges ?? {})
  ).map((range) => ({ ...range.model }));

  merges.forEach((m) => sheet.unMergeCells(m.top, m.left, m.bottom, m.right));
  sheet.insertRow(1, []);
  merges.forEach((m) => sheet.mergeCells(m.top + 1, m.left, m.bottom + 1, m.right));

  if (Array.isArray(sheet.views)) {
    sheet.views = sheet.views.map((view) =>
      view.state === "frozen" && typeof view.ySplit === "number" && view.ySplit > 0 ? { ...view, ySplit: view.ySplit + 1 } : view
    );
  }
}

/**
 * Inserts an empty row 1 and places the logo centred across the sheet's columns.
 * Call after the sheet is fully built (widths final). No-op when there is no logo.
 */
export async function addLogoToWorksheet(workbook: ExcelWorkbook, sheet: ExcelWorksheet) {
  const logo = await loadCompanyLogo();
  if (!logo) return;

  insertTopRowPreservingLayout(sheet);
  const rowHeightPt = 54;
  (sheet.getRow(1) as { height?: number }).height = rowHeightPt;

  const { width, height } = logoDisplaySize(logo);
  const colCount = Math.max(sheet.columnCount, 1);
  const widths = Array.from({ length: colCount }, (_, i) => Math.round((sheet.getColumn(i + 1).width ?? 8.43) * 7 + 5));
  const totalPx = widths.reduce((a, b) => a + b, 0);

  let remaining = Math.max(0, (totalPx - width) / 2);
  let col = 0;
  while (col < widths.length && remaining > widths[col]) {
    remaining -= widths[col];
    col += 1;
  }
  const colAnchor = col + (widths[col] ? remaining / widths[col] : 0);
  const rowPx = rowHeightPt * (96 / 72);
  const rowAnchor = Math.max(0, (rowPx - height) / 2) / rowPx;

  const imageId = workbook.addImage({ base64: logo.dataUrl, extension: logo.extension });
  sheet.addImage(imageId, { tl: { col: colAnchor, row: rowAnchor }, ext: { width, height }, editAs: "oneCell" });
}
